/**
 * Оркестрация сценария (раздел 3 ТЗ).
 *
 * Роутер не используется: сценарий линейный, а URL-навигация в PWA на планшете
 * скорее вредит — свайп «назад» посреди подписания выглядел бы как сбой. Шаг
 * хранится в состоянии, а точка возврата к незавершённой заявке — список заявок.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError, api, clearSession, getStoredUser, getToken } from './api/client'
import type { AppConfig, Application, Company, Tariff, TvPackage, User } from './api/types'
import { Alert, Button, Loading } from './components/ui'
import { preflightCameraPermission } from './lib/camera'
import { clearAllDrafts } from './lib/draft'
import { ApplicationsScreen } from './screens/ApplicationsScreen'
import { ConsentScreen } from './screens/ConsentScreen'
import { DoneScreen } from './screens/DoneScreen'
import { LoginScreen } from './screens/LoginScreen'
import { ReviewScreen } from './screens/ReviewScreen'
import { ScanScreen } from './screens/ScanScreen'
import { ServiceScreen } from './screens/ServiceScreen'
import { SignScreen } from './screens/SignScreen'

type View = 'list' | 'archive' | 'scan' | 'review' | 'service' | 'consent' | 'sign' | 'done'

/** Шаг, с которого логично продолжить незавершённую заявку. */
function resumeView(application: Application): View {
  if (application.status === 'signed' || application.status === 'ready_for_billing') return 'done'
  if (application.status === 'pending_signature') return 'sign'
  if (!application.last_name && !application.passport_number) return 'scan'
  if (application.missing_required.length > 0) {
    const passportGaps = application.missing_required.filter(
      (field) =>
        !['company_code', 'login', 'connection_address', 'tariff_code', 'phone', 'email'].includes(
          field,
        ),
    )
    return passportGaps.length > 0 ? 'review' : 'service'
  }
  return application.has_required_consents ? 'sign' : 'consent'
}

export function App() {
  const [user, setUser] = useState<User | null>(() => (getToken() ? getStoredUser() : null))
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [tariffs, setTariffs] = useState<Tariff[]>([])
  const [tvPackages, setTvPackages] = useState<TvPackage[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [bootError, setBootError] = useState<ApiError | null>(null)
  const [booted, setBooted] = useState(false)

  const [view, setView] = useState<View>('list')
  const [application, setApplication] = useState<Application | null>(null)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<ApiError | null>(null)
  const [openError, setOpenError] = useState<ApiError | null>(null)
  const [online, setOnline] = useState(navigator.onLine)

  // ── связь ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  const logout = useCallback(() => {
    clearSession()
    // Черновики содержат персональные данные — при выходе из учётки их не
    // оставляем на устройстве.
    clearAllDrafts()
    setUser(null)
    setApplication(null)
    setConfig(null)
    setCompanies([])
    setView('list')
    setBooted(false)
  }, [])

  // ── загрузка справочников после входа ────────────────────────────────────
  const boot = useCallback(async () => {
    setBootError(null)
    try {
      const [nextConfig, nextTariffs, nextTvPackages, nextCompanies] = await Promise.all([
        api.config(),
        api.tariffs(),
        api.tvPackages(),
        api.companies(),
      ])
      setConfig(nextConfig)
      setTariffs(nextTariffs)
      setTvPackages(nextTvPackages)
      setCompanies(nextCompanies)
      setBooted(true)
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка')
      if (apiError.unauthorized) {
        logout()
        return
      }
      setBootError(apiError)
      setBooted(true)
    }
  }, [logout])

  useEffect(() => {
    if (user && !config) void boot()
  }, [user, config, boot])

  // Разрешение на камеру спрашиваем заранее — сразу после входа, а не посреди
  // оформления заявки: так системный запрос всплывает один раз в спокойный
  // момент, и монтажник на шаге съёмки уже не упирается в него. В незащищённом
  // контексте (http:// в локальной сети) вызов молча ничего не делает —
  // просить там нечего, сработает съёмка камерой устройства.
  useEffect(() => {
    if (user) void preflightCameraPermission()
  }, [user])

  // ── переходы ─────────────────────────────────────────────────────────────
  const createApplication = async () => {
    setCreating(true)
    setCreateError(null)
    try {
      const created = await api.createApplication()
      setApplication(created)
      setView('scan')
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка')
      if (apiError.unauthorized) logout()
      else setCreateError(apiError)
    } finally {
      setCreating(false)
    }
  }

  const openApplication = async (id: string) => {
    setOpenError(null)
    try {
      const opened = await api.getApplication(id)
      setApplication(opened)
      setView(resumeView(opened))
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка')
      if (apiError.unauthorized) logout()
      else setOpenError(apiError)
    }
  }

  const toList = () => {
    setApplication(null)
    setView('list')
  }

  const advance = (next: View) => (updated: Application) => {
    setApplication(updated)
    setView(next)
  }

  // ── рендер ───────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <Shell online={online}>
        <LoginScreen onLoggedIn={setUser} />
      </Shell>
    )
  }

  if (!booted) {
    return (
      <Shell online={online} user={user} onLogout={logout}>
        <Loading text="Загружаем справочники…" />
      </Shell>
    )
  }

  if (bootError || !config) {
    return (
      <Shell online={online} user={user} onLogout={logout}>
        <div className="card">
          <Alert kind="error" title="Приложение не готово к работе" onRetry={boot}>
            {bootError?.message ?? 'Не удалось загрузить настройки.'}
          </Alert>
        </div>
      </Shell>
    )
  }

  return (
    <Shell
      online={online}
      user={user}
      onLogout={logout}
      title={application?.contract_number ?? undefined}
      onBackToList={view === 'list' || view === 'archive' ? undefined : toList}
    >
      {!config.docuseal_configured && (
        <Alert kind="warn" title="Подписание не настроено">
          На сервере не задан DOCUSEAL_API_TOKEN — форму и распознавание проверить можно, но
          договор подписать не получится.
        </Alert>
      )}
      {config.ocr_backend === 'stub' && (
        <Alert kind="warn" title="Распознавание в режиме заглушки">
          Сервер отдаёт тестовые данные паспорта (OCR_BACKEND=stub), а не результат
          распознавания.
        </Alert>
      )}

      {openError && (
        <Alert kind="error" title="Заявка не открылась" onDismiss={() => setOpenError(null)}>
          {openError.message}
        </Alert>
      )}

      {(view === 'list' || view === 'archive') && (
        <ApplicationsScreen
          archived={view === 'archive'}
          isAdmin={user.is_admin ?? false}
          onOpen={openApplication}
          onCreate={createApplication}
          onSwitch={() => setView(view === 'archive' ? 'list' : 'archive')}
          creating={creating}
          createError={createError}
          onDismissCreateError={() => setCreateError(null)}
        />
      )}

      {application && view === 'scan' && (
        <ScanScreen application={application} onDone={advance('review')} onBack={toList} />
      )}

      {application && view === 'review' && (
        <ReviewScreen
          application={application}
          onDone={advance('service')}
          onBack={toList}
          onRescan={() => setView('scan')}
        />
      )}

      {application && view === 'service' && (
        <ServiceScreen
          application={application}
          companies={companies}
          tariffs={tariffs}
          tvPackages={tvPackages}
          contractNumberAutogenerate={config.contract_number_autogenerate}
          loginHint={config.login_hint}
          catalog={config.catalog}
          addressBackend={config.address_backend}
          onCatalogRefreshed={() => void boot()}
          onDone={advance('consent')}
          onBack={() => setView('review')}
        />
      )}

      {application && view === 'consent' && (
        <ConsentScreen
          application={application}
          onDone={advance('sign')}
          onBack={() => setView('service')}
        />
      )}

      {application && view === 'sign' && (
        <SignScreen
          application={application}
          onSigned={advance('done')}
          onBack={() => setView('service')}
        />
      )}

      {application && view === 'done' && (
        <DoneScreen
          application={application}
          onNewApplication={createApplication}
          onToList={toList}
        />
      )}
    </Shell>
  )
}

// ── каркас страницы ──────────────────────────────────────────────────────────

interface ShellProps {
  children: React.ReactNode
  online: boolean
  user?: User
  onLogout?: () => void
  title?: string
  onBackToList?: () => void
}

function Shell({ children, online, user, onLogout, title, onBackToList }: ShellProps) {
  return (
    <div className="app">
      {!online && (
        <div className="offline-banner">
          Нет соединения. Введённые данные сохраняются на устройстве.
        </div>
      )}
      <header className="topbar">
        <h1>{title ? `Договор ${title}` : 'Подключение абонента'}</h1>
        {user && <span className="user">{user.full_name || user.login}</span>}
        {onBackToList && (
          <Button variant="ghost" onClick={onBackToList}>
            Заявки
          </Button>
        )}
        {onLogout && (
          <Button variant="ghost" onClick={onLogout}>
            Выйти
          </Button>
        )}
      </header>
      <main className="content">{children}</main>
    </div>
  )
}
